"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";

// Certificate data matching your website
const certificates = [
  { id: 1, title: "Award 1", image: "/assets/photos/award/Award-Photo-1.jpeg" },
  { id: 2, title: "Award 2", image: "/assets/photos/award/Award-Photo-2.jpeg" },
  { id: 3, title: "Award 3", image: "/assets/photos/award/Award-Photo-3.jpeg" },
  { id: 4, title: "Award 4", image: "/assets/photos/award/Award-Photo-4.jpeg" },
  { id: 5, title: "Award 5", image: "/assets/photos/award/Award-Photo-5.jpeg" },
  { id: 6, title: "Award 6", image: "/assets/photos/award/Award-Photo-6.jpeg" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const AwardsSection = () => {
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (certificate, index) => {
    setSelectedCertificate(certificate);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedCertificate(null);
    setCurrentIndex(0);
  };

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % certificates.length;
    setSelectedCertificate(certificates[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const goToPrev = () => {
    const prevIndex =
      (currentIndex - 1 + certificates.length) % certificates.length;
    setSelectedCertificate(certificates[prevIndex]);
    setCurrentIndex(prevIndex);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Escape") closeModal();
    if (e.key === "ArrowRight") goToNext();
    if (e.key === "ArrowLeft") goToPrev();
  };

  return (
    <>
      {/* Awards Section - Grid Layout */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-bold text-gray-800 text-center mb-2"
          >
            Awards & Certifications
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-md text-gray-600 text-center mb-4 max-w-3xl mx-auto"
          >
            Recognized for excellence in logistics and supply chain management
            with industry-leading certifications
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6"
          >
            {certificates.map((certificate, index) => (
              <motion.div
                key={certificate.id}
                variants={itemVariants}
                whileHover={{
                  scale: 1.05,
                  y: -5,
                  transition: { duration: 0.2 },
                }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                onClick={() => openModal(certificate, index)}
              >
                <div className="relative h-40 md:h-48 bg-gradient-to-br from-blue-100 to-gray-100 flex items-center justify-center p-2">
                  {/* Certificate Image Preview */}
                  <div className="relative w-full h-full flex items-center justify-center">
                    <Image
                      src={certificate.image}
                      alt={certificate.title}
                      width={600}
                      height={500}
                      // className=" object-cover"

                      className="w-full h-full object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-10 transition-opacity rounded-lg" />
                  </div>

                  {/* Hover Overlay */}
                  {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      <div className="bg-blue-600 bg-opacity-90 rounded-full p-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3-3H7" />
                        </svg>
                      </div>
                    </div>
                  </div> */}
                </div>
                <div className="p-3 bg-gradient-to-r from-blue-50 to-white">
                  {/* <p className="text-xs md:text-sm font-medium text-gray-800 text-center truncate">
                    {certificate.title}
                  </p> */}
                  <p className="text-xs text-gray-600 text-center group-hover:text-blue-600 transition-colors mt-1">
                    Click to view
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Certificate Modal */}
      <AnimatePresence>
        {selectedCertificate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <h3 className="text-xl font-bold">
                  {selectedCertificate.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-blue-500 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-2 md:p-6">
                <div className="relative flex items-center justify-center min-h-[50vh] md:min-h-[60vh] bg-gray-900 rounded-lg">
                  {/* Certificate Image */}
                  <Image
                    src={selectedCertificate.image}
                    alt={selectedCertificate.title}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    width={200}
                    height={200}
                  />

                  {/* Navigation Arrows */}
                  <button
                    onClick={goToPrev}
                    className="absolute left-2 md:left-4 p-2 md:p-3 bg-white bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all duration-300 transform hover:scale-110"
                    aria-label="Previous certificate"
                  >
                    <ChevronLeft size={24} className="text-gray-800" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-2 md:right-4 p-2 md:p-3 bg-white bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all duration-300 transform hover:scale-110"
                    aria-label="Next certificate"
                  >
                    <ChevronRight size={24} className="text-gray-800" />
                  </button>
                </div>

                {/* Certificate Info */}
                <div className="mt-4 text-center">
                  <p className="text-gray-600 font-medium">
                    Award {selectedCertificate.id} of {certificates.length}
                  </p>
                </div>
              </div>

              {/* Thumbnail Navigation */}
              <div className="p-4 border-t bg-gray-50">
                <div className="flex justify-center space-x-2 overflow-x-auto py-2">
                  {certificates.map((cert, index) => (
                    <button
                      key={cert.id}
                      onClick={() => {
                        setSelectedCertificate(cert);
                        setCurrentIndex(index);
                      }}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        currentIndex === index
                          ? "border-blue-600 scale-110"
                          : "border-gray-300 hover:border-blue-400"
                      }`}
                    >
                      <Image
                        src={cert.image}
                        alt={cert.title}
                        width={200}
                        height={200}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between items-center p-4 md:p-6 border-t bg-gray-50">
                <span className="text-sm text-gray-600 hidden md:block">
                  Certificate ID: QIL-
                  {selectedCertificate.id.toString().padStart(3, "0")}
                </span>
                <div className="flex gap-2 w-full md:w-auto justify-center">
                  <button
                    onClick={goToPrev}
                    className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium flex items-center space-x-2"
                  >
                    <ChevronLeft size={18} />
                    <span>Previous</span>
                  </button>
                  <button
                    onClick={goToNext}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
                  >
                    <span>Next</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AwardsSection;
